/**
 * Fetches the full eval item name map from Supabase once per team.
 * Also loads agent escalation flags for a given agentId.
 * Used to display human-readable eval tag names in MessageBubble.
 */
import { useState, useEffect } from "react"
import { supabase } from "@/lib/data"
import type { EvalItemMap } from "@/pages/dev/moderation/components/MessageBubble"

export function useEvalItemMap(teamId: string | undefined): EvalItemMap {
  const [map, setMap] = useState<EvalItemMap>({})

  useEffect(() => {
    if (!teamId) return
    supabase
      .from("eval_items")
      .select("id, name, highrisk, lowconf, askhuman")
      .then(({ data }) => {
        if (!data) return
        const m: EvalItemMap = {}
        for (const item of data) {
          if (item.id == null) continue
          m[item.id as number] = {
            name: item.name ?? `Item ${item.id}`,
            highrisk: item.highrisk,
            lowconf: item.lowconf,
            askhuman: item.askhuman,
          }
        }
        setMap(m)
      })
  }, [teamId])

  return map
}

export function useAgentFlags(agentId: string | undefined): AgentEscalationFlag[] {
  const [flags, setFlags] = useState<AgentEscalationFlag[]>([])

  useEffect(() => {
    if (!agentId) { setFlags([]); return }
    const id = parseInt(agentId, 10)
    if (isNaN(id)) { setFlags([]); return }
    supabase
      .from("agents")
      .select("config")
      .eq("id", id)
      .single()
      .then(({ data }) => {
        const config = data?.config as any
        setFlags(config?.escalation_flags || [])
      })
  }, [agentId])

  return flags
}
